import React, { useState, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiEye } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { 
  useSubscriptions, 
  useDeleteSubscription, 
  useCreateSubscription, 
  useUpdateSubscription,
  useSubscriptionById 
} from '../hooks/useSubscriptions';

const Subscriptions = ({ user }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch subscriptions data using React Query
  const {
    data: subscriptionsResponse,
    isLoading,
    error,
    isFetching,
  } = useSubscriptions(pagination.current, pagination.pageSize);

  // Mutations
  const deleteItemMutation = useDeleteSubscription();

  // Memoized data
  const { subscriptionsData, totalRecords } = useMemo(() => {
    if (!subscriptionsResponse) {
      return { subscriptionsData: [], totalRecords: 0 };
    }

    const data = subscriptionsResponse.data?.data || [];
    const total = subscriptionsResponse.data?.total || 0;

    return {
      subscriptionsData: data,
      totalRecords: total
    };
  }, [subscriptionsResponse]);

  // Check if delete mutation is loading
  const isMutating = deleteItemMutation.isPending;

  // Get error message from mutations or query
  const errorMessage = error?.message || 
                      deleteItemMutation.error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'name',
      title: 'Name',
      render: (value, row) => (
        <div className="max-w-md">
          <div className="text-sm text-gray-900">
            <div>{value?.en || 'N/A'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'monthly_price',
      title: 'Monthly Price',
      render: (value, row) => (
        <span className="text-sm text-gray-900">
          {value ? `${value} ${row.currency?.symbol || ''}` : 'N/A'}
        </span>
      ),
    },
    {
      key: 'yearly_price',
      title: 'Yearly Price',
      render: (value, row) => (
        <span className="text-sm text-gray-900">
          {value ? `${value} ${row.currency?.symbol || ''}` : 'N/A'}
        </span>
      ),
    },
    {
      key: 'max_view_profile_per_month',
      title: 'Max Profile Views/Month',
      render: (value, row) => (
        <span className="text-sm text-gray-900">
          {value || 'N/A'}
        </span>
      ),
    },
    {
      key: 'max_events_number_per_month',
      title: 'Max Events/Month',
      render: (value, row) => (
        <span className="text-sm text-gray-900">
          {value || 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="View Details"
          >
            <FiEye className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEditItem(row)}
            className="text-primary-500 hover:text-primary-800 p-1"
            title="Edit"
            disabled={isMutating}
          >
            <FiEdit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteItem(row)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete"
            disabled={isMutating}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  // Handle pagination change
  const handlePageChange = (page) => {
    setPagination({ ...pagination, current: page });
  };

  const handlePageSizeChange = (pageSize, current) => {
    setPagination({ current, pageSize });
  };

  const handleAddItem = () => {
    setSelectedItem(null);
    setShowAddModal(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setShowEditModal(true);
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  const handleDeleteItem = async (item) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'bg-primary-500 text-white hover:bg-primary-600',
          cancelButton: 'bg-red-500 text-white hover:bg-red-600'
        }
      });

      if (result.isConfirmed) {
        await deleteItemMutation.mutateAsync(item.id);
        Swal.fire({
          title: 'Deleted!',
          text: 'Subscription has been deleted.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire('Error!', error.message, 'error');
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDetailsModal(false);
    setSelectedItem(null);
  };

  const handleSuccess = () => {
    handleModalClose();
    Swal.fire({
      title: 'Success!',
      text: 'Subscription saved successfully.',
      icon: 'success',
      timer: 2000,
      showConfirmButton: false,
    });
  };

  return (
    <Layout user={user}>
      <div className="space-y-0">
        <PageHeader
          title="Subscriptions"
          description="Manage subscription plans"
          action={{
            label: 'Add Subscription',
            onClick: handleAddItem,
            icon: <FiPlus className="h-4 w-4" />,
          }}
        />

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="flex justify-end !mb-4">
          <button
            onClick={handleAddItem}
            className="bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 flex items-center gap-2"
          >
            <FiPlus className="h-4 w-4" /> Add Subscription
          </button>
        </div>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={subscriptionsData}
          loading={isLoading || isFetching || isMutating}
          filterable={false}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />

        {/* Add Modal */}
        {showAddModal && (
          <AddSubscription
            onClose={handleModalClose}
            onSuccess={handleSuccess}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <EditSubscription
            item={selectedItem}
            onClose={handleModalClose}
            onSuccess={handleSuccess}
          />
        )}

        {/* Details Modal */}
        {showDetailsModal && (
          <SubscriptionDetails
            item={selectedItem}
            onClose={handleModalClose}
          />
        )}
      </div>
    </Layout>
  );
};

// Add Subscription Component
const AddSubscription = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: { en: '', ar: 'test' },
    monthly_price: '',
    yearly_price: '',
    max_view_profile_per_month: '',
    max_events_number_per_month: '',
    currency_id: 1,
    featured_items: [
      { ar: '', en: '' },
      { ar: '', en: '' },
      { ar: '', en: '' }
    ]
  });

  const createItemMutation = useCreateSubscription();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.name.en.trim()) {
        Swal.fire('Error!', 'Please fill in the English name.', 'error');
        return;
      }

      const submitData = {
        ...formData,
        monthly_price: parseFloat(formData.monthly_price),
        yearly_price: parseFloat(formData.yearly_price),
        max_view_profile_per_month: parseInt(formData.max_view_profile_per_month),
        max_events_number_per_month: parseInt(formData.max_events_number_per_month),
      };

      await createItemMutation.mutateAsync(submitData);
      onSuccess();
    } catch (error) {
      Swal.fire('Error!', error.message, 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Add Subscription</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={formData.name.en}
              onChange={(e) => setFormData({ 
                ...formData, 
                name: { ...formData.name, en: e.target.value } 
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Price
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.monthly_price}
                onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yearly Price
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.yearly_price}
                onChange={(e) => setFormData({ ...formData, yearly_price: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Profile Views per Month
              </label>
              <input
                type="number"
                value={formData.max_view_profile_per_month}
                onChange={(e) => setFormData({ ...formData, max_view_profile_per_month: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Events per Month
              </label>
              <input
                type="number"
                value={formData.max_events_number_per_month}
                onChange={(e) => setFormData({ ...formData, max_events_number_per_month: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Items
            </label>
            <div className="space-y-3">
              {formData.featured_items.map((item, index) => (
                <div key={index} className="flex gap-3 items-center">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder={`Feature ${index + 1} (English)`}
                      value={item.en}
                      onChange={(e) => {
                        const newFeaturedItems = [...formData.featured_items];
                        newFeaturedItems[index] = { ...newFeaturedItems[index], en: e.target.value, ar: e.target.value };
                        setFormData({ ...formData, featured_items: newFeaturedItems });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newFeaturedItems = formData.featured_items.filter((_, i) => i !== index);
                      setFormData({ ...formData, featured_items: newFeaturedItems });
                    }}
                    className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                    disabled={formData.featured_items.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newFeaturedItems = [...formData.featured_items, { ar: '', en: '' }];
                  setFormData({ ...formData, featured_items: newFeaturedItems });
                }}
                className="px-4 py-2 text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50"
              >
                Add Feature
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createItemMutation.isPending}
              className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
            >
              {createItemMutation.isPending ? 'Adding...' : 'Add Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Subscription Component
const EditSubscription = ({ item, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: { en: item?.name?.en || '', ar: item?.name?.ar || 'test' },
    monthly_price: item?.monthly_price || '',
    yearly_price: item?.yearly_price || '',
    max_view_profile_per_month: item?.max_view_profile_per_month || '',
    max_events_number_per_month: item?.max_events_number_per_month || '',
    currency_id: item?.currency_id || 1,
    featured_items: item?.featured_items || [
      { ar: '', en: '' },
      { ar: '', en: '' },
      { ar: '', en: '' }
    ]
  });

  const updateItemMutation = useUpdateSubscription();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.name.en.trim()) {
        Swal.fire({title: 'Error!', text: 'Please fill in the English name.', icon: 'error'});
        return;
      }

      const submitData = {
        ...formData,
        monthly_price: parseFloat(formData.monthly_price),
        yearly_price: parseFloat(formData.yearly_price),
        max_view_profile_per_month: parseInt(formData.max_view_profile_per_month),
        max_events_number_per_month: parseInt(formData.max_events_number_per_month),
      };

      await updateItemMutation.mutateAsync({ id: item.id, data: submitData });
      onSuccess();
    } catch (error) {
      Swal.fire({title: 'Error!', text: error.message, icon: 'error',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
            confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
            cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
          }
        });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Edit Subscription</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={formData.name.en}
              onChange={(e) => setFormData({ 
                ...formData, 
                name: { ...formData.name, en: e.target.value } 
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Price
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.monthly_price}
                onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yearly Price
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.yearly_price}
                onChange={(e) => setFormData({ ...formData, yearly_price: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Profile Views per Month
              </label>
              <input
                type="number"
                value={formData.max_view_profile_per_month}
                onChange={(e) => setFormData({ ...formData, max_view_profile_per_month: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Events per Month
              </label>
              <input
                type="number"
                value={formData.max_events_number_per_month}
                onChange={(e) => setFormData({ ...formData, max_events_number_per_month: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Items
            </label>
            <div className="space-y-3">
              {formData.featured_items.map((item, index) => (
                <div key={index} className="flex gap-3 items-center">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder={`Feature ${index + 1} (English)`}
                      value={item.en}
                      onChange={(e) => {
                        const newFeaturedItems = [...formData.featured_items];
                        newFeaturedItems[index] = { ...newFeaturedItems[index], en: e.target.value, ar: e.target.value };
                        setFormData({ ...formData, featured_items: newFeaturedItems });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newFeaturedItems = formData.featured_items.filter((_, i) => i !== index);
                      setFormData({ ...formData, featured_items: newFeaturedItems });
                    }}
                    className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50"
                    disabled={formData.featured_items.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const newFeaturedItems = [...formData.featured_items, { ar: '', en: '' }];
                  setFormData({ ...formData, featured_items: newFeaturedItems });
                }}
                className="px-4 py-2 text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md hover:bg-blue-50"
              >
                Add Feature
              </button>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateItemMutation.isPending}
              className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
            >
              {updateItemMutation.isPending ? 'Updating...' : 'Update Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Subscription Details Component
const SubscriptionDetails = ({ item, onClose }) => {
  const { data: subscriptionDetails, isLoading } = useSubscriptionById(item?.id);

  const details = subscriptionDetails?.data || item;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Subscription Details</h2>
        
        {isLoading ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID
                </label>
                <p className="text-sm text-gray-900">{details.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <p className="text-sm text-gray-900">{details.currency?.name || 'N/A'}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <p className="text-sm text-gray-900">{details.name?.en || 'N/A'}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Price
                </label>
                <p className="text-sm text-gray-900">
                  {details.monthly_price ? `${details.monthly_price} ${details.currency?.symbol || ''}` : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yearly Price
                </label>
                <p className="text-sm text-gray-900">
                  {details.yearly_price ? `${details.yearly_price} ${details.currency?.symbol || ''}` : 'N/A'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Profile Views per Month
                </label>
                <p className="text-sm text-gray-900">{details.max_view_profile_per_month || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Events per Month
                </label>
                <p className="text-sm text-gray-900">{details.max_events_number_per_month || 'N/A'}</p>
              </div>
            </div>

            {/* Featured Items */}
            {details.featured_items && details.featured_items.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Featured Items
                </label>
                <div className="space-y-2">
                  {details.featured_items.map((item, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-900">
                        {item.en || 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created At
                </label>
                <p className="text-sm text-gray-900">
                  {details.created_at ? new Date(details.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Updated At
                </label>
                <p className="text-sm text-gray-900">
                  {details.updated_at ? new Date(details.updated_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
